import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useBackgroundJobs } from '../context/BackgroundJobsContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const StatusBadge = ({ status }) => {
  const map = {
    pending:    'bg-amber-100 text-amber-700 border-amber-200',
    processing: 'bg-blue-100 text-blue-700 border-blue-200',
    ready:      'bg-emerald-100 text-emerald-700 border-emerald-200',
    approved:   'bg-emerald-100 text-emerald-700 border-emerald-200',
    rejected:   'bg-rose-100 text-rose-700 border-rose-200',
  };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border capitalize flex-shrink-0 ${map[status] || 'bg-slate-100 text-slate-500 border-slate-200'}`}>
      {status}
    </span>
  );
};

const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
        copied
          ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
          : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-violet-50 hover:text-violet-700 hover:border-violet-200'
      }`}
    >
      {copied ? (
        <>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          {label || 'Copy'}
        </>
      )}
    </button>
  );
};

const UserEventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addDeletionJob } = useBackgroundJobs();

  const [event, setEvent] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('upload');

  // Delete batch state
  const [deletingBatchId, setDeletingBatchId] = useState(null);

  // Change PIN state
  const [showChangePinModal, setShowChangePinModal] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [pinError, setPinError] = useState('');

  // Rename event state
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState('');

  // Banner state
  const bannerInputRef = useRef(null);
  const [bannerUploading, setBannerUploading] = useState(false);

  const baseUrl = window.location.origin;
  const uploadUrl = event ? `${baseUrl}/upload/${event.upload_token}` : '';
  const memoriesUrl = event ? `${baseUrl}/verify/${event.verify_token}` : '';
  const memoriesOgUrl = event ? `${baseUrl}/preview/${event.verify_token}` : '';

  useEffect(() => {
    fetchEventAndBatches();
  }, [id]);

  const fetchEventAndBatches = async () => {
    setLoading(true);
    try {
      const [evRes, batchRes] = await Promise.all([
        axios.get(`${API_URL}/events/${id}`),
        axios.get(`${API_URL}/events/batches`),
      ]);
      setEvent(evRes.data);
      // Filter batches for this event
      const eventBatches = (batchRes.data || []).filter(
        (b) => b.event_id != null && Number(b.event_id) === Number(id)
      );
      setBatches(eventBatches);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const toggleUploadLink = async () => {
    try {
      const res = await axios.patch(`${API_URL}/events/${id}/upload-link/toggle`);
      setEvent((prev) => ({ ...prev, upload_link_active: res.data.upload_link_active }));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to toggle');
    }
  };

  const toggleVerifyLink = async () => {
    try {
      const res = await axios.patch(`${API_URL}/events/${id}/verify-link/toggle`);
      setEvent((prev) => ({ ...prev, verify_link_active: res.data.verify_link_active }));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to toggle');
    }
  };

  const handleChangePin = async (e) => {
    e.preventDefault();
    const pin = newPin.replace(/\D/g, '');
    if (pin.length !== 6) { setPinError('PIN must be exactly 6 digits'); return; }
    setPinError('');
    try {
      const res = await axios.put(`${API_URL}/events/${id}`, { pin });
      setEvent((prev) => ({ ...prev, pin: res.data.pin }));
      setShowChangePinModal(false);
      setNewPin('');
    } catch (err) {
      setPinError(err.response?.data?.error || 'Failed to update PIN');
    }
  };

  const handleRenameEvent = async (e) => {
    e.preventDefault();
    const name = (renameValue || '').trim();
    if (!name) { setRenameError('Event name is required'); return; }
    setRenameError('');
    try {
      const res = await axios.put(`${API_URL}/events/${id}`, { name });
      setEvent((prev) => ({ ...prev, name: res.data.name }));
      setShowRenameModal(false);
      setRenameValue('');
    } catch (err) {
      setRenameError(err.response?.data?.error || 'Failed to update event name');
    }
  };

  const handleDeleteBatch = async (batchId, batchNumber, photoCount) => {
    if (!window.confirm(`Delete Batch #${batchNumber}? This will permanently remove all ${photoCount} photos.`)) return;
    setDeletingBatchId(batchId);
    try {
      await axios.delete(`${API_URL}/batches/${batchId}`);
      addDeletionJob({
        batchId,
        total: photoCount ?? 0,
        label: `Batch #${batchNumber} - ${event?.name || ''}`,
        eventName: event?.name || null,
      });
      setBatches((prev) => prev.filter((b) => b.id !== batchId));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete batch');
    } finally {
      setDeletingBatchId(null);
    }
  };

  const handleBannerUpload = async (file) => {
    if (!file) return;
    setBannerUploading(true);
    try {
      const fd = new FormData();
      fd.append('banner', file);
      const res = await axios.post(`${API_URL}/events/${id}/banner`, fd);
      setEvent((prev) => ({ ...prev, banner_image: res.data.banner_image }));
    } catch (err) {
      alert(err.response?.data?.error || 'Banner upload failed');
    } finally {
      setBannerUploading(false);
    }
  };

  const handleBannerRemove = async () => {
    setBannerUploading(true);
    try {
      await axios.delete(`${API_URL}/events/${id}/banner`);
      setEvent((prev) => ({ ...prev, banner_image: null }));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove banner');
    } finally {
      setBannerUploading(false);
    }
  };

  // Analytics derived from event data
  const analytics = event ? {
    page_visits:       event.page_visits ?? 0,
    face_verified:     event.face_verified_count ?? event.verification_count ?? 0,
    download_image:    event.download_image_count ?? 0,
    download_zip:      event.download_zip_count ?? 0,
    view_all_photo:    event.view_all_photo_count ?? 0,
    batch_count:       event.batch_count ?? batches.length,
    photo_count:       event.photo_count ?? 0,
  } : {};

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto flex justify-center items-center min-h-[40vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading event…</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-8 text-center">
          <p className="text-rose-700 font-medium mb-4">{error || 'Event not found'}</p>
          <button onClick={() => navigate('/events')} className="text-sm text-violet-600 hover:underline">← Back to My Events</button>
        </div>
      </div>
    );
  }

  const uploadActive = event.upload_link_active === true || event.upload_link_active === 1;
  const verifyActive = event.verify_link_active === true || event.verify_link_active === 1;

  const pendingBatches   = batches.filter(b => b.status === 'pending');
  const processingBatches = batches.filter(b => b.status === 'processing');
  const approvedBatches  = batches.filter(b => b.status === 'approved');

  const displayStatusForBatch = (batch) => {
    if (!batch) return '';
    if (batch.status === 'pending') return 'pending';
    if (batch.status === 'approved') {
      if (batch.approved_count > 0 && batch.unencoded_count > 0) return 'processing';
      if (batch.approved_count > 0 && batch.unencoded_count === 0) return 'ready';
      return 'ready';
    }
    return batch.status;
  };

  const toggleShowAllPhotoTabDetail = async () => {
    if (!event) return;
    const next = !(event.show_all_photo_tab === true || event.show_all_photo_tab === 1);
    try {
      await axios.put(`${API_URL}/events/${id}`, { show_all_photo_tab: next });
      setEvent((prev) => prev ? { ...prev, show_all_photo_tab: next } : prev);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update setting');
    }
  };

  const tabs = [
    { key: 'upload',      label: '1. Upload',      icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12' },
    { key: 'memories',    label: '2. Memories',    icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { key: 'engagement',  label: '3. Engagement',  icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
  ];

  return (
    <div className="max-w-4xl mx-auto">

      {/* Back nav */}
      <div className="mb-5">
        <button
          onClick={() => navigate('/events')}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          My Events
        </button>
      </div>

      {/* Event header card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-5">

        {/* Banner */}
        {event.banner_image ? (
          <div className="relative group">
            <img src={event.banner_image} alt="Event banner" className="w-full h-40 object-cover" />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => bannerInputRef.current?.click()}
                disabled={bannerUploading}
                className="px-3 py-1.5 rounded-xl bg-white/90 text-xs font-semibold text-slate-700 hover:bg-white transition-colors"
              >Change</button>
              <button
                type="button"
                onClick={handleBannerRemove}
                disabled={bannerUploading}
                className="px-3 py-1.5 rounded-xl bg-rose-500/90 text-xs font-semibold text-white hover:bg-rose-600 transition-colors"
              >Remove</button>
            </div>
            {bannerUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => bannerInputRef.current?.click()}
            disabled={bannerUploading}
            className="w-full h-20 border-b border-dashed border-slate-200 flex items-center justify-center gap-2 text-xs text-slate-400 hover:bg-violet-50/30 hover:text-violet-500 transition-colors"
          >
            {bannerUploading ? (
              <div className="w-4 h-4 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Add event banner
              </>
            )}
          </button>
        )}
        <input
          ref={bannerInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => handleBannerUpload(e.target.files?.[0])}
        />

        {/* Event name + stats */}
        <div className="px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-900 truncate">{event.name}</h1>
              <button
                type="button"
                onClick={() => { setShowRenameModal(true); setRenameValue(event.name || ''); setRenameError(''); }}
                className="p-1 rounded-lg text-slate-300 hover:text-violet-600 hover:bg-violet-50 transition-colors flex-shrink-0"
                title="Edit event name"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            </div>
            {event.created_at && (
              <p className="text-xs text-slate-400 mt-0.5">
                Created {new Date(event.created_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}
              </p>
            )}
            {/* Quick stats */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{analytics.batch_count} batches</span>
              <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{analytics.photo_count} photos</span>
              {analytics.face_verified > 0 && (
                <span className="text-xs bg-violet-50 text-violet-600 px-2 py-0.5 rounded-full">{analytics.face_verified} verified</span>
              )}
              {pendingBatches.length > 0 && (
                <Link
                  to="/batches"
                  className="inline-flex items-center gap-1 text-xs font-bold text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full animate-pulse"
                >
                  {pendingBatches.length} pending approval
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 mb-5 bg-slate-100 p-1 rounded-2xl">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === tab.key
                ? 'bg-white text-violet-700 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
            </svg>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: Upload ── */}
      {activeTab === 'upload' && (
        <div className="space-y-4">

          {/* Upload link card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800">1. Upload</h3>
                  <p className="text-xs text-slate-400">Share with guests to collect photos · PIN required</p>
                  {event.pin != null && (
                    <div className="mt-1 inline-flex items-center gap-1.5 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5">
                      <svg className="w-3 h-3 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <span className="font-mono text-xs font-semibold text-violet-700 tracking-widest">{String(event.pin)}</span>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(String(event.pin)); }}
                        className="text-[10px] text-violet-400 hover:text-violet-600"
                        title="Copy PIN"
                      >
                        Copy
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowChangePinModal(true); setNewPin(''); setPinError(''); }}
                        className="text-[10px] text-violet-400 hover:text-violet-600"
                        title="Change PIN"
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={toggleUploadLink}
                className={`text-xs px-3 py-1 rounded-full font-semibold transition-colors ${
                  uploadActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'
                }`}
              >
                {uploadActive ? 'Active' : 'Inactive'}
              </button>
            </div>

            {/* URL row */}
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 mb-3">
              <code className="text-xs text-slate-600 font-mono flex-1 truncate">{uploadUrl}</code>
              <CopyButton text={uploadUrl} label="Copy link" />
            </div>

            {/* Share buttons */}
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span className="text-xs text-slate-400 font-medium">Share via:</span>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Upload your photos here: ${uploadUrl}\nPIN: ${event.pin}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                WhatsApp
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(uploadUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                Facebook
              </a>
            </div>

            {/* Self-upload action */}
            <div className="mt-1 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-slate-400">
                Or upload photos yourself using the same link.
              </p>
              <button
                type="button"
                onClick={() => window.open(uploadUrl, '_blank')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md active:scale-95"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Upload my photos
              </button>
            </div>
          </div>

          {/* Batches section */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-800">Batches</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {batches.length === 0
                    ? 'No batches yet — guests upload photos via the link above'
                    : `${batches.length} batch${batches.length !== 1 ? 'es' : ''}`}
                </p>
              </div>
              {pendingBatches.length > 0 && (
                <Link
                  to="/batches"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-amber-800 bg-amber-100 border border-amber-300 hover:bg-amber-200 transition-colors animate-pulse"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  {pendingBatches.length} need approval
                </Link>
              )}
            </div>

            {batches.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center bg-slate-100">
                  <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <p className="text-sm text-slate-500">No batches yet</p>
                <p className="text-xs text-slate-400 mt-1">Batches appear when guests upload photos via the upload link.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {batches.map((batch) => {
                  const isDeleting = deletingBatchId === batch.id;
                  return (
                    <div
                      key={batch.id}
                      className="px-5 py-3.5 flex items-center gap-3 hover:bg-slate-50 cursor-pointer rounded-xl transition-colors"
                      onClick={() => navigate('/batches', { state: { batchId: batch.id } })}
                    >
                      {/* Batch icon */}
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        batch.status === 'approved' ? 'bg-emerald-100' :
                        batch.status === 'pending'  ? 'bg-amber-100' :
                        'bg-slate-100'
                      }`}>
                        <svg className={`w-4.5 h-4.5 ${
                          batch.status === 'approved' ? 'text-emerald-600' :
                          batch.status === 'pending'  ? 'text-amber-600' :
                          'text-slate-500'
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>

                      {/* Batch info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-slate-800">Batch #{batch.batch_number}</span>
                          <StatusBadge status={displayStatusForBatch(batch)} />
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {batch.photo_count} photo{batch.photo_count !== 1 ? 's' : ''}
                          {batch.rejected_count > 0 && ` · ${batch.rejected_count} rejected`}
                          {batch.pending_count > 0 && ` · ${batch.pending_count} pending`}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/batches', { state: { batchId: batch.id } });
                          }}
                          className="p-1.5 rounded-lg text-slate-300 hover:bg-slate-100 hover:text-violet-600 transition-colors"
                          title="View photos"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteBatch(batch.id, batch.batch_number, batch.photo_count)}
                          disabled={isDeleting}
                          className="p-1.5 rounded-lg text-slate-300 hover:bg-rose-50 hover:text-rose-500 transition-colors disabled:opacity-50"
                          title="Delete batch"
                        >
                          {isDeleting ? (
                            <div className="w-4 h-4 border-2 border-rose-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB 2: Memories ── */}
      {activeTab === 'memories' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #ede9fe, #e0e7ff)' }}>
                  <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800">2. Memories</h3>
                  <p className="text-xs text-slate-400">Guests use this to find their photos using their selfie</p>
                </div>
              </div>
              <button
                type="button"
                onClick={toggleVerifyLink}
                className={`text-xs px-3 py-1 rounded-full font-semibold transition-colors ${
                  verifyActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'
                }`}
              >
                {verifyActive ? 'Active' : 'Inactive'}
              </button>
            </div>

            {/* URL */}
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 mb-4">
              <code className="text-xs text-slate-600 font-mono flex-1 truncate">{memoriesOgUrl}</code>
              <CopyButton text={memoriesOgUrl} label="Copy link" />
            </div>

            {/* All Photos tab toggle */}
            <div className="flex items-start gap-3 mb-4 p-3 bg-slate-50 rounded-xl border border-slate-100">
              <input
                type="checkbox"
                id="detail-show-all-photo-tab"
                checked={event.show_all_photo_tab === true || event.show_all_photo_tab === 1}
                onChange={toggleShowAllPhotoTabDetail}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
              />
              <label htmlFor="detail-show-all-photo-tab" className="text-xs text-slate-600 cursor-pointer">
                Show &quot;All Photos&quot; tab on the find-my-photos page — guests can browse all event photos (view only).
              </label>
            </div>

            {/* Share options */}
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2.5 uppercase tracking-wider">Share via</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent('Find your photos from ' + event.name + ': ' + memoriesOgUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-green-50 border border-green-200 hover:bg-green-100 transition-colors group"
                >
                  <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  <span className="text-xs font-semibold text-green-700">WhatsApp</span>
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(memoriesOgUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors"
                >
                  <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  <span className="text-xs font-semibold text-blue-700">Facebook</span>
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(memoriesOgUrl)}&text=${encodeURIComponent('Find your photos from ' + event.name)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors"
                >
                  <svg className="w-6 h-6 text-slate-700" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  <span className="text-xs font-semibold text-slate-700">X / Twitter</span>
                </a>
                <button
                  type="button"
                  onClick={() => {
                    if (navigator.share) {
                      navigator
                        .share({ title: event.name, url: memoriesOgUrl })
                        .catch(() => {
                          // User canceled or share not completed; ignore.
                        });
                    } else {
                      navigator.clipboard.writeText(memoriesOgUrl);
                      alert('Link copied!');
                    }
                  }}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-violet-50 border border-violet-200 hover:bg-violet-100 transition-colors"
                >
                  <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  <span className="text-xs font-semibold text-violet-700">More</span>
                </button>
              </div>
            </div>
          </div>

          {/* Info box */}
          <div className="bg-violet-50 border border-violet-200 rounded-2xl p-4 flex items-start gap-3">
            <svg className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-violet-800 mb-0.5">How memories work</p>
              <p className="text-xs text-violet-600 leading-relaxed">
                Guests open this link, take a selfie, and our face recognition finds all their photos from the event. Make sure batches are approved and encoded so photos are searchable.
              </p>
              <p className="text-xs text-violet-600 leading-relaxed mt-1">
                Show &quot;All Photos&quot; tab on the find-my-photos page — guests can browse all event photos (view only).
              </p>
              {approvedBatches.length === 0 && batches.length > 0 && (
                <p className="text-xs text-amber-700 font-semibold mt-2">
                  ⚠ No approved batches yet — approve batches first so guests can find their photos.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: Engagement ── */}
      {activeTab === 'engagement' && (
        <div className="space-y-4">

          {/* Summary stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Page Visits',    value: analytics.page_visits,    color: 'bg-slate-100',   iconColor: 'text-slate-500',   icon: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' },
              { label: 'Face Verified',  value: analytics.face_verified,  color: 'bg-violet-100',  iconColor: 'text-violet-600',  icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
              { label: 'Img Downloads', value: analytics.download_image,  color: 'bg-blue-100',    iconColor: 'text-blue-600',    icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' },
              { label: 'ZIP Downloads', value: analytics.download_zip,    color: 'bg-emerald-100', iconColor: 'text-emerald-600', icon: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4' },
            ].map(({ label, value, color, iconColor, icon }) => (
              <div key={label} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                  <svg className={`w-5 h-5 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={icon} />
                  </svg>
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 leading-none">{value}</p>
                  <p className="text-xs font-medium text-slate-500 mt-0.5">{label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Detailed analytics table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-800">3. Engagement / Usage</h3>
            </div>
            <div className="divide-y divide-slate-100">
              {[
                { label: 'Total Batches',        value: analytics.batch_count,     icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', color: 'text-slate-500' },
                { label: 'Total Photos',          value: analytics.photo_count,     icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z', color: 'text-slate-500' },
                { label: 'Page Visits',           value: analytics.page_visits,     icon: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z', color: 'text-slate-500' },
                { label: 'Face Verified',         value: analytics.face_verified,   icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', color: 'text-violet-600' },
                { label: 'Image Downloads',       value: analytics.download_image,  icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4', color: 'text-blue-600' },
                { label: 'ZIP Downloads',         value: analytics.download_zip,    icon: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4', color: 'text-emerald-600' },
                { label: 'All Photos Views',      value: analytics.view_all_photo,  icon: 'M4 6h16M4 10h16M4 14h16M4 18h16', color: 'text-slate-500' },
              ].map(({ label, value, icon, color }) => (
                <div key={label} className="px-5 py-3.5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <svg className={`w-4 h-4 ${color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                    </svg>
                    <span className="text-sm text-slate-600">{label}</span>
                  </div>
                  <span className={`text-sm font-bold ${value > 0 ? 'text-slate-900' : 'text-slate-300'}`}>{value ?? 0}</span>
                </div>
              ))}
            </div>
          </div>

          {/* No data hint */}
          {Object.values(analytics).every(v => !v || v === 0) && (
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-center">
              <p className="text-sm text-slate-500">No engagement data yet.</p>
              <p className="text-xs text-slate-400 mt-1">Share your memories link with guests to start tracking engagement.</p>
            </div>
          )}
        </div>
      )}

      {/* Change PIN modal */}
      {showChangePinModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-1">Change PIN</h2>
            <p className="text-sm text-slate-500 mb-4">{event.name}</p>
            {pinError && <p className="text-rose-600 text-sm mb-3 bg-rose-50 px-3 py-2 rounded-lg">{pinError}</p>}
            <form onSubmit={handleChangePin} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">New 6-digit PIN</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-center text-lg tracking-widest font-mono outline-none transition-shadow"
                  placeholder="123456"
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowChangePinModal(false); setNewPin(''); setPinError(''); }}
                  className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                >Cancel</button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 text-white rounded-xl text-sm font-semibold"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
                >Update PIN</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rename event modal */}
      {showRenameModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-1">Rename event</h2>
            <p className="text-sm text-slate-500 mb-4">Update the event name shown to guests.</p>
            {renameError && <p className="text-rose-600 text-sm mb-3 bg-rose-50 px-3 py-2 rounded-lg">{renameError}</p>}
            <form onSubmit={handleRenameEvent} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Event name</label>
                <input
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none transition-shadow text-sm"
                  placeholder="e.g. Annual Day 2025"
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowRenameModal(false); setRenameValue(''); setRenameError(''); }}
                  className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 text-white rounded-xl text-sm font-semibold"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
                >
                  Save name
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default UserEventDetail;
